import { cn } from '../../../lib/utils';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, FileText, Mail, Loader2 } from 'lucide-react';
import api from '../../../api/axios'

const LevyPreviewModal = ({ isOpen, onClose, data, isDark = false }) => {
    const [activeTab, setActiveTab] = useState('document');
    const [pdfUrl, setPdfUrl] = useState(null);
    const [emailData, setEmailData] = useState(null);
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [loadingEmail, setLoadingEmail] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            if (data) {
                setActiveTab('document');
                fetchPdf();
                if (data.recipient_kind === 'E') {
                    fetchEmail();
                }
            }
        } else {
            document.body.style.overflow = 'unset';
            setPdfUrl(null);
            setEmailData(null);
            setError(null);
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, data]);

    const fetchPdf = async () => {
        setLoadingPdf(true);
        setError(null);
        try {
            const response = await api.get(`/api/levy-automations/${data.id}/document`);
            if (response.data && response.data.url) {
                setPdfUrl(response.data.url);
            } else {
                setError('Failed to load document URL');
            }
        } catch (err) {
            setError('Error loading document: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoadingPdf(false);
        }
    };

    const fetchEmail = async () => {
        setLoadingEmail(true);
        try {
            const response = await api.get(`/api/levy-automations/${data.id}/email`);
            if (response.data && response.data.data) {
                setEmailData(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching email data', err);
            // It's possible the email wasn't sent via Front, so we don't throw a fatal error.
            setEmailData({ body: 'Email content not found.', subject: 'No subject' });
        } finally {
            setLoadingEmail(false);
        }
    };

    if (!isOpen || !data) return null;

    const hasEmail = data.recipient_kind === 'E';

    const modalContent = (
        <div
            className={cn("fixed inset-0 bg-black/80 flex items-center justify-center p-[16px]")}
            style={{ zIndex: 99999 }}
            onClick={onClose}
        >
            <div
                className={cn(
                    "rounded-[10px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] p-[20px] flex flex-col gap-[16px] w-full max-w-[1000px] h-[90vh] transition-colors duration-200",
                    isDark ? 'bg-[#161515]' : 'bg-white'
                )}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className={cn("flex items-center justify-between border-b border-[#e5e5e5] dark:border-[#333] pb-[16px]")}>
                    <div className={cn("flex items-center gap-[16px]")}>
                        <h2 className={cn("text-xl font-semibold m-0", isDark ? 'text-white' : 'text-[#072837]')}>
                            Preview: {data.plan_number} / Lot {data.lot_number}
                        </h2>
                    </div>
                    <div className="flex items-center gap-[12px]">
                        {activeTab === 'document' && pdfUrl && (
                            <button
                                onClick={() => window.open(pdfUrl, '_blank')}
                                className={`rounded-[8px] px-[12px] py-[6px] flex items-center gap-[6px] border-0 cursor-pointer transition-colors whitespace-nowrap ${
                                    isDark ? 'bg-[#f5f5f5] text-[#000000] border border-[#292929] hover:bg-[#e8e8e8]' : 'bg-[#072837] text-white hover:bg-[#0a3a4d]'
                                }`}
                                title="Download Document"
                            >
                                <div
                                    className="w-[14px] h-[14px] sm:w-[16px] sm:h-[16px]"
                                    style={{
                                        backgroundColor: isDark ? '#000000' : '#ffffff',
                                        WebkitMaskImage: "url('/assets/icons/download.svg')",
                                        WebkitMaskRepeat: 'no-repeat',
                                        WebkitMaskSize: 'contain',
                                        WebkitMaskPosition: 'center',
                                        maskImage: "url('/assets/icons/download.svg')",
                                        maskRepeat: 'no-repeat',
                                        maskSize: 'contain',
                                        maskPosition: 'center',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                    aria-hidden="true"
                                />
                                <span className="font-medium text-[14px] sm:text-[16px] transition-colors duration-200">Download</span>
                            </button>
                        )}
                        {activeTab === 'email' && data.front_conversation_id && (
                            <a
                                href={`https://app.frontapp.com/open/${data.front_conversation_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`rounded-[8px] px-[12px] py-[6px] flex items-center gap-[6px] border-0 cursor-pointer transition-colors whitespace-nowrap no-underline bg-[#cd6d2c] ${isDark ? 'hover:bg-white' : 'hover:bg-[#072837]'}`}
                            >
                                <div
                                    className="w-[14px] h-[14px] sm:w-[16px] sm:h-[16px]"
                                    style={{
                                        backgroundColor: isDark ? '#161515' : '#ffffff',
                                        WebkitMaskImage: "url('/assets/icons/front.svg')",
                                        WebkitMaskRepeat: 'no-repeat',
                                        WebkitMaskSize: 'contain',
                                        WebkitMaskPosition: 'center',
                                        maskImage: "url('/assets/icons/front.svg')",
                                        maskRepeat: 'no-repeat',
                                        maskSize: 'contain',
                                        maskPosition: 'center',
                                        transition: 'background-color 0.2s ease'
                                    }}
                                    aria-hidden="true"
                                />
                                <span className={`font-medium text-[14px] sm:text-[16px] transition-colors duration-200 ${isDark ? 'text-[#161515]' : 'text-white'}`}>Open in Front</span>
                            </a>
                        )}
                        <button
                            onClick={onClose}
                            className={cn(
                                "flex items-center justify-center w-[32px] h-[32px] p-0 rounded-full border-0 cursor-pointer transition-colors",
                                isDark ? 'bg-[#333] hover:bg-[#444]' : 'bg-gray-100 hover:bg-gray-200'
                            )}
                            aria-label="Close"
                        >
                            <X className={cn("w-[20px] h-[20px]", isDark ? 'text-white' : 'text-[#072837]')} />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                {hasEmail && (
                    <div className={cn(
                        "border-b transition-colors duration-200",
                        isDark ? 'border-[#292929]' : 'border-[#d9d9d9]'
                    )}>
                        <div className={cn("flex items-center gap-2 sm:gap-3 lg:gap-[16px] flex-wrap")}>
                            <p className={cn(
                                "text-[13px] sm:text-[14px] font-semibold m-0 transition-colors duration-200",
                                isDark ? 'text-white' : 'text-[#072837]'
                            )}>View:</p>
                            <div className={cn("flex gap-2 sm:gap-[8px] flex-wrap")}>
                                <button
                                    onClick={() => setActiveTab('document')}
                                    className={cn(
                                        "px-2.5 py-1.5 text-[13px] sm:px-[12px] sm:py-[6px] sm:text-[15px] lg:text-[16px] font-medium border-solid border-[1px] rounded-[6px] cursor-pointer flex items-center gap-2 transition-all duration-200",
                                        activeTab === 'document'
                                            ? isDark
                                                ? 'bg-[#cd6d2c] text-[#000000] border-[#cd6d2c]'
                                                : 'bg-[#072837] text-white border-[#072837]'
                                            : isDark
                                                ? 'bg-[#161515] text-[#cd6d2c] border-[#cd6d2c] hover:bg-[#cd6d2c] hover:text-[#000000]'
                                                : 'bg-white text-[#072837] border-[#d9d9d9] hover:bg-[#f5f5f5]'
                                    )}
                                >
                                    <FileText size={18} />
                                    Document
                                </button>
                                <button
                                    onClick={() => setActiveTab('email')}
                                    className={cn(
                                        "px-2.5 py-1.5 text-[13px] sm:px-[12px] sm:py-[6px] sm:text-[15px] lg:text-[16px] font-medium border-solid border-[1px] rounded-[6px] cursor-pointer flex items-center gap-2 transition-all duration-200",
                                        activeTab === 'email'
                                            ? isDark
                                                ? 'bg-[#cd6d2c] text-[#000000] border-[#cd6d2c]'
                                                : 'bg-[#072837] text-white border-[#072837]'
                                            : isDark
                                                ? 'bg-[#161515] text-[#cd6d2c] border-[#cd6d2c] hover:bg-[#cd6d2c] hover:text-[#000000]'
                                                : 'bg-white text-[#072837] border-[#d9d9d9] hover:bg-[#f5f5f5]'
                                    )}
                                >
                                    <Mail size={18} />
                                    Email
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Content Area */}
                <div className={cn("flex-1 overflow-auto bg-gray-50 dark:bg-[#1a1a1a] rounded-[8px] relative")}>
                    {activeTab === 'document' && (
                        <div className={cn("w-full h-full flex items-center justify-center")}>
                            {loadingPdf ? (
                                <div className={cn("flex flex-col items-center gap-2")}>
                                    <Loader2 className={cn("w-8 h-8 animate-spin text-blue-600")} />
                                    <span className={cn(isDark ? 'text-gray-400' : 'text-gray-600')}>Loading document...</span>
                                </div>
                            ) : error ? (
                                <div className={cn("text-red-500 font-medium")}>{error}</div>
                            ) : pdfUrl ? (
                                <iframe
                                    src={`${pdfUrl}#toolbar=0`}
                                    className={cn("w-full h-full border-0")}
                                    title="PDF Document"
                                />
                            ) : (
                                <div className={cn("text-gray-500")}>No document available</div>
                            )}
                        </div>
                    )}

                    {activeTab === 'email' && (
                        <div className={cn("w-full h-full p-[20px] overflow-auto")}>
                            {loadingEmail ? (
                                <div className={cn("flex flex-col items-center justify-center h-full gap-2")}>
                                    <Loader2 className={cn("w-8 h-8 animate-spin text-blue-600")} />
                                    <span className={cn(isDark ? 'text-gray-400' : 'text-gray-600')}>Loading email...</span>
                                </div>
                            ) : emailData ? (
                                <div className={cn("flex flex-col gap-6")}>
                                    <div className={cn("flex flex-col gap-[6px]")}>
                                        <div className={cn("flex gap-[6px] items-center")}>
                                            <p className={cn("font-semibold text-[16px] m-0", isDark ? 'text-white' : 'text-[#072837]')}>Subject:</p>
                                            <p className={cn(
                                                "font-normal text-[16px] m-0",
                                                isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'
                                            )}>{emailData.subject}</p>
                                        </div>
                                        <div className={cn("flex gap-[6px] items-center")}>
                                            <p className={cn("font-semibold text-[16px] m-0", isDark ? 'text-white' : 'text-[#072837]')}>Sent to:</p>
                                            <p className={cn(
                                                "font-normal text-[16px] m-0",
                                                isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'
                                            )}>{emailData.sentTo}</p>
                                        </div>
                                    </div>
                                    <div
                                        className={cn(
                                            "font-normal text-[16px] leading-normal",
                                            isDark ? 'text-[#f2f2f2]' : 'text-[#072837]'
                                        )}
                                        dangerouslySetInnerHTML={{ __html: emailData.body }}
                                    />
                                </div>
                            ) : (
                                <div className={cn("text-gray-500 h-full flex items-center justify-center")}>No email available</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default LevyPreviewModal;
